import React, { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import {
  Search, ShoppingCart, ChevronLeft, ChevronRight, ChevronDown, Star,
  Plus, Minus, X, Check, MapPin, Globe, Package, ArrowRight, LayoutGrid,
  Store as StoreIcon, AlertTriangle, Tag,
} from "lucide-react";

import type { AppDispatch, RootState } from "../../../redux/store";
import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
} from "../../../redux/productSlice";
import { addToCart, decrementCartItem, clearCart } from "../../../redux/cartSlice";
import { listenToBrandsByBusinessType } from "../../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../../redux/productRatingSlice";
import { CurrencyContext } from "../../../GLOBAL/CurrencyContext";
import { electronicsCategories } from "../../../CATEGORIES/ElectronicsCategories";
import {
  Sizes,
  ShoeSizes,
  productColors,
} from "../../../CATEGORIES/ATTRIBUTES/ProductsAttributes";
import appLogo from "../../../assets/app/logo.png";

/* ═══════════════════════════════════════════════════════════
   ELECTRONICS BUSINESS TYPE — every store/partner query and
   category grouping on this page is scoped to this id.
═══════════════════════════════════════════════════════════ */
const BUSINESS_TYPE_ID = 11;

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS — Facebook-style blue / white / black
═══════════════════════════════════════════════════════════ */
const T = {
  pageBg: "#F0F2F5",
  cardBg: "#FFFFFF",
  headerBg: "#FFFFFF",

  black: "#050505",
  ink: "#050505",
  inkMid: "#1C1E21",
  muted: "#65676B",
  mutedSoft: "#8A8D91",
  mutedDim: "#B0B3B8",

  blue: "#1877F2",
  blueDark: "#0E4FA8",
  bluePale: "#E7F0FE",

  border: "rgba(5,5,5,0.09)",
  divider: "rgba(5,5,5,0.06)",

  grad: "linear-gradient(135deg,#1877F2 0%,#0E4FA8 100%)",
  gradH: "linear-gradient(90deg,#1877F2 0%,#0E4FA8 100%)",
};
const RGBA_DARK = (a: number) => `rgba(5,5,5,${a})`;
const RGBA_BLUE = (a: number) => `rgba(24,119,242,${a})`;

/* Minimal country → continent map — same lookup used across the app. */
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe", "czech republic": "Europe",
  hungary: "Europe", romania: "Europe", croatia: "Europe", iceland: "Europe",

  "united states": "North America", canada: "North America", mexico: "North America",

  brazil: "South America", argentina: "South America", chile: "South America",
  peru: "South America", colombia: "South America",

  nigeria: "Africa", kenya: "Africa", "south africa": "Africa", egypt: "Africa",
  morocco: "Africa", ghana: "Africa", tanzania: "Africa", ethiopia: "Africa",

  china: "Asia", japan: "Asia", india: "Asia", thailand: "Asia",
  "united arab emirates": "Asia", "south korea": "Asia", indonesia: "Asia",
  vietnam: "Asia", "saudi arabia": "Asia", israel: "Asia", turkey: "Asia",

  australia: "Oceania", "new zealand": "Oceania", fiji: "Oceania",
};
const getContinent = (country?: string | null): string | null => {
  if (!country) return null;
  return COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null;
};

/* tier 0 = same city, 1 = same country, 2 = same continent, 3 = rest of world
   LOCAL  → tier 0-1, GLOBAL → tier 2-3. Disjoint sets, same as GroceriesHome. */
const isLocalTier = (tier: number) => tier <= 1;
const isGlobalTier = (tier: number) => tier >= 2;

/* ═══════════════════════════════════════════════════════════
   SHOP-BY-CATEGORY GROUPS — same 5 groups the page already used,
   now purely a FILTER (select/toggle), never a navigation target.
═══════════════════════════════════════════════════════════ */
const CATEGORY_GROUPS = [
  { title: "Phones, Tablets, Laptops, Computers", accent: T.blue, ids: [20000001, 20000020, 20000021, 20000002] },
  { title: "TVs, Audio, Gaming, Entertainment", accent: T.blueDark, ids: [20000003, 20000022, 20000004, 20000006] },
  { title: "Cameras, Wearables, Smart Home, GPS", accent: T.blue, ids: [20000005, 20000015, 20000024, 20000016] },
  { title: "Appliances, Kitchen, Security, Car", accent: T.blueDark, ids: [20000007, 20000008, 20000014, 20000010] },
  { title: "Office, Tools, Accessories, eBooks", accent: T.blue, ids: [20000012, 20000011, 20000025, 20000017] },
].map(group => ({
  ...group,
  items: group.ids
    .map(id => electronicsCategories.find((c: any) => c.id === id))
    .filter(Boolean) as any[],
}));

const electronicsColorGroup = { values: productColors[0]?.values ?? [] };
const electronicsSizeGroup = { values: [...Sizes, ...ShoeSizes] };

const fmtPrice = (n: number, sym = "€", rate = 1) =>
  `${sym}${(Number(n ?? 0) * rate).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** "3D sphere" swatch look — same recipe used elsewhere in the app. */
function sphere3D(hex: string): string {
  return [
    `radial-gradient(circle at 35% 32%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.10) 28%, transparent 52%)`,
    `radial-gradient(circle at 68% 72%, rgba(0,0,0,0.30) 0%, transparent 55%)`,
    `radial-gradient(circle at 50% 50%, ${hex} 0%, ${hex} 100%)`,
  ].join(",");
}

/* Distinct cart line id per colour/size combination, so two variants
   of the same listing never collide in the cart. */
const buildCartId = (item: any, colorId: number | null, sizeId: number | null) => {
  if (colorId == null && sizeId == null) return item.listingId;
  return `${item.listingId}__${colorId ?? "nc"}__${sizeId ?? "ns"}`;
};

const getVariantMode = (item: any): "fashion" | "beauty" | null => {
  if (!item?.variants || !Object.keys(item.variants).length) return null;
  if (Object.values(item.variants).some((v: any) => Array.isArray(v.sizeIds) && v.sizeIds.length)) return "fashion";
  if (Object.values(item.variants).some((v: any) => v.imageUrl && (!v.sizeIds || !v.sizeIds.length))) return "beauty";
  return null;
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
*{box-sizing:border-box;}
::-webkit-scrollbar{display:none;}
@keyframes ehp-grad{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
@keyframes ehp-pulse{0%,100%{opacity:1}50%{opacity:.35}}

.eh-card{transition:box-shadow .2s, transform .2s;}
.eh-card:hover{box-shadow:0 14px 36px ${RGBA_BLUE(0.16)};transform:translateY(-4px);}
.eh-plus{transition:transform .15s;}
.eh-plus:hover{transform:scale(1.08);}
.eh-plus:active{transform:scale(.92);}

.eh-hbtn{display:flex;align-items:center;gap:6px;background:rgba(255,255,255,.16);
         border:1px solid rgba(255,255,255,.3);cursor:pointer;color:#fff;
         font-size:12px;font-weight:700;padding:9px 14px;border-radius:999px;
         flex-shrink:0;white-space:nowrap;transition:background .2s;font-family:inherit;}
.eh-hbtn:hover{background:rgba(255,255,255,.26);}

.eh-scg-card{flex-shrink:0;background:#fff;border-radius:20px;overflow:hidden;
            border:1.5px solid ${T.border};box-shadow:0 6px 28px rgba(5,5,5,.06);
            transition:box-shadow .22s,border-color .22s,transform .22s;}
.eh-scg-tile{background:none;border:none;cursor:pointer;padding:0;border-radius:14px;overflow:hidden;}
.eh-scg-tile-inner{width:100%;aspect-ratio:1/1;border-radius:14px;overflow:hidden;position:relative;
                  background:${T.bluePale};transition:transform .22s;}
.eh-scg-tile:hover .eh-scg-tile-inner{transform:scale(1.03);}
.eh-scg-tile.sel .eh-scg-tile-inner{outline:2.5px solid var(--sel-accent);outline-offset:2px;}

.eh-backdrop{position:fixed;inset:0;background:${RGBA_DARK(0.5)};z-index:1000;}
.eh-add-btn{transition:transform .15s ease, box-shadow .15s ease;}
.eh-add-btn:hover{transform:translateY(-2px);}
.eh-add-btn:active{transform:translateY(0) scale(.96);}
.eh-opt-row{transition:border-color .15s, background .15s;}
.eh-opt-row:hover{border-color:${RGBA_BLUE(0.5)};}

.eh-color-cell{display:flex;flex-direction:column;align-items:center;gap:5px;cursor:pointer;background:none;border:none;padding:0;}
.eh-color-sphere{width:34px;height:34px;border-radius:50%;flex-shrink:0;transition:transform .18s;
                    box-shadow:inset -5px -4px 10px rgba(0,0,0,.28),inset 4px 4px 8px rgba(255,255,255,.18),0 4px 14px rgba(0,0,0,.18);}
.eh-color-cell:hover .eh-color-sphere{transform:scale(1.1) translateY(-2px);}
.eh-color-sphere.sel{outline:2.5px solid ${T.blue};outline-offset:2px;}
.eh-color-name{font-size:9.5px;font-weight:600;color:${T.muted};text-align:center;max-width:56px;}
.eh-color-cell.sel .eh-color-name{color:${T.blue};font-weight:800;}
`;

interface CardBadge { label: string; bg: string; }

const ElectronicsHome: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);

  const authUser = useSelector((s: RootState) => s.auth.user);
  const resolvedUserId = userId || authUser?.userId || "";

  const listings         = useSelector((s: RootState) => s.products.products);
  const filteredListings = useSelector((s: RootState) => (s as any).products.filteredProducts);
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);
  const cartItems        = useSelector((s: RootState) => s.cart.items);
  const ratingSummaries  = useSelector((s: RootState) => (s as any).productRatings?.summaries ?? {});

  const [query, setQuery]           = useState("");
  const [isMobile, setIsMobile]     = useState(window.innerWidth <= 768);
  const [searchFocused, setSearchFocused] = useState(false);

  /* category filter — SELECTABLE, never navigates (grocery's own logic) */
  const [activeCat, setActiveCat] = useState<number | null>(null);
  const [shopCatExpanded, setShopCatExpanded] = useState(true);

  /* category panels */
  const [catOpen, setCatOpen]               = useState(false);
  const [desktopCatOpen, setDesktopCatOpen] = useState(false);
  const [openGroupIdx, setOpenGroupIdx]     = useState<number | null>(null);

  /* location */
  const [userCity, setUserCity]       = useState<string | null>(null);
  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [showGlobal, setShowGlobal]   = useState(false);
  const [locLoading, setLocLoading]   = useState(true);
  const [locDenied, setLocDenied]     = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);

  /* detail sheet */
  const [selectedItem, setSelectedItem] = useState<any | null>(null);

  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => { dispatch(listenToPublishedProducts()); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries() as any); }, [dispatch]);
  useEffect(() => {
    const unsub = dispatch(listenToBrandsByBusinessType(BUSINESS_TYPE_ID) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [dispatch]);

  /* geolocation — identical pattern to GroceriesHome */
  useEffect(() => {
    if (!navigator.geolocation) { setLocDenied(true); setLocLoading(false); return; }
    navigator.geolocation.getCurrentPosition(
      pos => {
        setLocLoading(false);
        if ((window as any).google?.maps) {
          new (window as any).google.maps.Geocoder().geocode(
            { location: { lat: pos.coords.latitude, lng: pos.coords.longitude } },
            (res: any, st: string) => {
              if (st === "OK" && res?.[0]) {
                const comps = res[0].address_components;
                const get   = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                setUserCountry(get("country"));
              }
            }
          );
        }
      },
      () => { setLocDenied(true); setLocLoading(false); }
    );
  }, []);

  useEffect(() => {
    dispatch(filterLocalProductsByCountry(null) as any);
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  }, [userCountry, showGlobal, dispatch]);

  const locationActive = !locDenied && !locLoading && userCountry !== null;

  const partners = useMemo(() => {
    return (commercePartners || []).filter((p: any) => p.categoryId === BUSINESS_TYPE_ID);
  }, [commercePartners]);

  /* only stores that actually have at least one published product */
  const partnersWithProducts = useMemo(() => {
    const brandIdsWithProducts = new Set(listings.map((l: any) => l.brandId || l.hostId));
    return partners.filter((p: any) => brandIdsWithProducts.has(p.brandId));
  }, [partners, listings]);

  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    partners.forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [partners]);

  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    partners.forEach((p: any) => {
      const brandCity = p.address?.city?.toLowerCase().trim();
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && brandCity && brandCity === deviceCity) tier = 0;
      else if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [partners, userCity, userCountry]);

  /* stores ranked by proximity, used by the Trending row — same
     disjoint LOCAL/GLOBAL split as GroceriesHome. */
  const trendingStores = useMemo(() => {
    let list = [...partnersWithProducts];
    if (locationActive) {
      list = list.filter((p: any) => {
        const tier = brandTier.get(p.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    list.sort((a: any, b: any) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
    return list.slice(0, 16);
  }, [partnersWithProducts, brandTier, showGlobal, locationActive]);

  const handleToggleGlobal = () => setShowGlobal(v => !v);

  /* category select (toggle) — FILTERS the list below, never navigates */
  const handleCatSelect = (id: number) => setActiveCat(prev => (prev === id ? null : id));

  const activeCatName = useMemo(() => {
    if (activeCat == null) return null;
    for (const g of CATEGORY_GROUPS) {
      const found = g.items.find((c: any) => c.id === activeCat);
      if (found) return found.name;
    }
    return null;
  }, [activeCat]);

  /* products (category + search), grouped by store below */
  const brandProducts = useMemo(() => {
    let list = (filteredListings ?? listings).filter((l: any) => l.isPublished !== false);

    if (activeCat != null) {
      list = list.filter((l: any) =>
        l.categoryId === activeCat || l.productId === activeCat || l.productVariantId === activeCat
      );
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((l: any) =>
        (l.name ?? "").toLowerCase().includes(q) || (l.description ?? "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeCat, listings, query, filteredListings]);

  /* same LOCAL (tier 0-1) / GLOBAL (tier 2-3) split applied per brand
     group, so the grid and the Trending Stores row always agree. */
  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    brandProducts.forEach((item: any) => {
      const bId = item.brandId || item.hostId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map(bId => ({ brandId: bId, items: map.get(bId)! }));

    if (locationActive) {
      groups = groups.filter(g => {
        const tier = brandTier.get(g.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    groups = [...groups].sort((a, b) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
    return groups;
  }, [brandProducts, showGlobal, brandTier, locationActive]);

  const getQty = (cartId: string) => cartItems.find((i: any) => i.listingId === cartId)?.qty ?? 0;

  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  const handleAdd = (item: any, colorId: number | null, sizeId: number | null, e?: React.MouseEvent) => {
    e?.stopPropagation();
    ensureCartBrandCompat(item.brandId);
    const cartId = buildCartId(item, colorId, sizeId);
    const photo = (colorId != null && item.variants?.[colorId]?.imageUrl) || item.photos?.[0];
    dispatch(addToCart({
      listingId: cartId,
      baseListingId: item.listingId,
      name: item.name ?? "",
      rate: item.rate ?? 0,
      photo, hostId: item.hostId, brandId: item.brandId,
      colorId: colorId ?? undefined,
      sizeId: sizeId ?? undefined,
    } as any));
  };
  const handleRemove = (item: any, colorId: number | null, sizeId: number | null, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const cartId = buildCartId(item, colorId, sizeId);
    dispatch(decrementCartItem({ listingId: cartId }));
  };

  const cartTotal = cartItems.reduce((s: number, i: any) => s + i.qty, 0);
  const goToCheckout = () => navigate(`/productcheckout/${resolvedUserId}`, { state: { items: cartItems } });
  const goToBrand    = (id: string) => navigate(`/StoreHome/${id}`);

  const openDetails = (item: any) => setSelectedItem(item);
  const closeDetails = () => setSelectedItem(null);

  useEffect(() => {
    if (!selectedItem) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [selectedItem]);

  /* ═══════════════════════════════════
     PRODUCT CARD — FastFoodStore's square-card anatomy: image,
     badge, name, sub-line, price + circular "+" button. Colour/size
     variants behave like FastFoodStore's portion sizes: the "+"
     opens the detail sheet instead of adding straight to cart.
  ═══════════════════════════════════ */
  const ProductCard: React.FC<{ item: any }> = ({ item }) => {
    const hasVariants = getVariantMode(item) !== null;
    const cartId  = buildCartId(item, null, null);
    const q       = hasVariants ? 0 : getQty(cartId);
    const price   = fmtPrice(item.rate ?? 0, symbol ?? "€", rate ?? 1);
    const summary = ratingSummaries[item.listingId];
    const badge: CardBadge | null = item.discount > 0 ? { label: `-${item.discount}%`, bg: T.blueDark } : null;

    return (
      <div className="eh-card" onClick={() => openDetails(item)}
        style={{ background: T.cardBg, border: `1.5px solid ${T.border}`, borderRadius: 16, padding: 14, cursor: "pointer", position: "relative", display: "flex", flexDirection: "column" }}>
        {badge && (
          <span style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: badge.bg, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 6, letterSpacing: ".02em" }}>
            {badge.label}
          </span>
        )}
        <div style={{ width: "100%", aspectRatio: "1/1", borderRadius: 12, overflow: "hidden", background: T.bluePale, marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {item.photos?.[0]
            ? <img src={item.photos[0]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <Package size={32} color={T.blue} style={{ opacity: .4 }} />}
        </div>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: T.black, lineHeight: 1.3, marginBottom: 2, fontFamily: "'Inter',sans-serif", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", minHeight: 34 }}>
          {item.name}
        </div>
        <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 10 }}>
          {item.brandName || "Electronics"}
          {summary?.totalReviews ? ` · ${summary.avgRating.toFixed(1)}★ (${summary.totalReviews})` : ""}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto" }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: T.black }}>
            {hasVariants ? "From " : ""}{price}
          </span>
          {hasVariants ? (
            <button className="eh-plus" onClick={e => { e.stopPropagation(); openDetails(item); }}
              style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: T.blue, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: `0 3px 10px ${RGBA_BLUE(0.35)}` }}>
              <Plus size={16} color="#fff" />
            </button>
          ) : q > 0 ? (
            <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 6, background: T.bluePale, borderRadius: 999, padding: "3px 6px" }}>
              <button className="eh-plus" onClick={e => handleRemove(item, null, null, e)} style={{ width: 22, height: 22, borderRadius: "50%", border: "none", background: T.blue, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Minus size={12} />
              </button>
              <span style={{ fontSize: 12, fontWeight: 800, color: T.black, minWidth: 12, textAlign: "center" }}>{q}</span>
              <button className="eh-plus" onClick={e => handleAdd(item, null, null, e)} style={{ width: 22, height: 22, borderRadius: "50%", border: "none", background: T.blue, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Plus size={13} />
              </button>
            </div>
          ) : (
            <button className="eh-plus" onClick={e => handleAdd(item, null, null, e)}
              style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: T.blue, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: `0 3px 10px ${RGBA_BLUE(0.35)}` }}>
              <Plus size={16} color="#fff" />
            </button>
          )}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     INLINE DETAIL VIEW — bottom sheet (mobile) / centered modal
     (desktop), same anatomy as FastFoodStore's DetailView. Colour
     acts like a radio group, size acts like a second radio group
     (fashion mode); beauty-mode items only ever show colour.
  ═══════════════════════════════════ */
  const DetailView = () => {
    const item = selectedItem;
    const mode = item ? getVariantMode(item) : null;
    const colorIds: number[] = (mode && item ? Object.keys(item.variants || {}).map(Number) : []);
    const [selectedColor, setSelectedColor] = React.useState<number | null>(colorIds.length ? colorIds[0] : null);
    const [selectedSize, setSelectedSize]   = React.useState<number | null>(null);

    React.useEffect(() => {
      if (!item) return;
      setSelectedColor(colorIds.length ? colorIds[0] : null);
      setSelectedSize(null);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item?.listingId]);

    if (!item) return null;

    const sizeIds: number[] = (mode === "fashion" && selectedColor != null)
      ? (item.variants?.[selectedColor]?.sizeIds ?? [])
      : [];

    const requiresColor = mode !== null && selectedColor == null;
    const requiresSize  = mode === "fashion" && selectedColor != null && sizeIds.length > 0 && selectedSize == null;
    const requiresPick  = requiresColor || requiresSize;

    const activePhoto = (selectedColor != null && item.variants?.[selectedColor]?.imageUrl) || item.photos?.[0];
    const cartId = buildCartId(item, selectedColor, selectedSize);
    const qty    = getQty(cartId);
    const price  = fmtPrice(item.rate ?? 0, symbol ?? "€", rate ?? 1);
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;

    const Content = () => (
      <>
        <div style={{ position: "relative", width: "100%", aspectRatio: "4/3", background: T.bluePale, flexShrink: 0 }}>
          {activePhoto ? (
            <img src={activePhoto} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Package size={48} color={T.blue} style={{ opacity: 0.4 }} />
            </div>
          )}
          <button onClick={closeDetails} style={{ position: "absolute", top: 10, right: 10, background: "rgba(255,255,255,.92)", border: "none", width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: `0 2px 8px ${RGBA_DARK(0.18)}` }}>
            <X size={16} color={T.black} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px 8px" }}>
          {item.brandName && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.muted }}>{item.brandName}</span>
            </div>
          )}

          <h2 style={{ fontFamily: "'Inter',sans-serif", fontWeight: 800, fontSize: 20, color: T.black, marginBottom: 8, lineHeight: 1.25 }}>
            {item.name}
          </h2>

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", background: T.gradH, backgroundSize: "200% 100%", animation: "ehp-grad 3s ease infinite", borderRadius: 999, padding: "4px 14px", fontWeight: 900, color: "#fff", fontSize: 16 }}>
              {price}
            </span>
            {hasRating && (
              <div style={{ display: "flex", alignItems: "center", gap: 3, background: T.bluePale, borderRadius: 999, padding: "4px 10px" }}>
                <Star size={12} fill={T.blue} color={T.blue} />
                <span style={{ fontSize: 12, fontWeight: 800, color: T.black }}>{summary.avgRating.toFixed(1)}</span>
                <span style={{ fontSize: 11, color: T.muted }}>({summary.totalReviews.toLocaleString()})</span>
              </div>
            )}
          </div>

          {/* ── COLOUR — single choice ── */}
          {colorIds.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", color: T.muted, marginBottom: 8, textTransform: "uppercase" }}>
                Choose a colour
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {colorIds.map((cid) => {
                  const c = electronicsColorGroup.values.find((v: any) => v.id === cid);
                  const hex = (c as any)?.hex ?? (c as any)?.hexCodes?.[0];
                  const on = selectedColor === cid;
                  return (
                    <button key={cid} type="button" className={`eh-color-cell${on ? " sel" : ""}`}
                      onClick={() => { setSelectedColor(cid); setSelectedSize(null); }}>
                      <div className={`eh-color-sphere${on ? " sel" : ""}`} style={{ background: hex ? sphere3D(hex) : sphere3D(T.mutedDim) }} />
                      <span className="eh-color-name">{c?.name ?? ""}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── SIZE — single choice, radio-style (fashion mode only) ── */}
          {sizeIds.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", color: T.muted, marginBottom: 8, textTransform: "uppercase" }}>
                Choose a size
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {sizeIds.map((sid) => {
                  const s = electronicsSizeGroup.values.find((v: any) => v.id === sid);
                  const on = selectedSize === sid;
                  return (
                    <button key={sid} className="eh-opt-row" onClick={() => setSelectedSize(sid)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "11px 14px", borderRadius: 12, border: `1.5px solid ${on ? T.blue : T.border}`, background: on ? RGBA_BLUE(0.06) : "#fff", cursor: "pointer" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${on ? T.blue : T.mutedDim}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {on && <span style={{ width: 9, height: 9, borderRadius: "50%", background: T.blue }} />}
                        </span>
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: T.black }}>{s?.name ?? sid}</span>
                      </span>
                      {on && <Check size={14} color={T.blue} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {item.description && (
            <p style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.7, marginBottom: 16 }}>{item.description}</p>
          )}
        </div>

        <div style={{ padding: "12px 20px 18px", borderTop: `1px solid ${T.border}`, background: "#fff", flexShrink: 0 }}>
          {qty > 0 ? (
            <div className="eh-add-btn" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", height: 50, background: "#fff", border: `2px solid ${T.blue}`, borderRadius: 999, padding: "0 8px" }}>
              <button onClick={() => handleRemove(item, selectedColor, selectedSize)} style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: T.blue, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                <Minus size={16} color="#fff" />
              </button>
              <span style={{ color: T.blue, fontWeight: 900, fontSize: 17 }}>{qty} in cart</span>
              <button onClick={() => handleAdd(item, selectedColor, selectedSize)} style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: T.blue, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                <Plus size={16} color="#fff" />
              </button>
            </div>
          ) : (
            <button className="eh-add-btn" disabled={requiresPick}
              onClick={() => !requiresPick && handleAdd(item, selectedColor, selectedSize)}
              style={{ width: "100%", height: 50, borderRadius: 999, border: "none", background: requiresPick ? T.mutedDim : T.grad, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: requiresPick ? "not-allowed" : "pointer", boxShadow: requiresPick ? "none" : `0 6px 20px ${RGBA_BLUE(0.3)}` }}>
              <ShoppingCart size={17} color="#fff" />
              <span style={{ color: "#fff", fontWeight: 800, fontSize: 14, letterSpacing: "0.04em" }}>
                {requiresColor ? "SELECT A COLOUR" : requiresSize ? "SELECT A SIZE" : `ADD TO CART · ${price}`}
              </span>
            </button>
          )}
        </div>
      </>
    );

    return createPortal(
      <AnimatePresence>
        <motion.div key="eh-backdrop" className="eh-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeDetails} />
        {isMobile ? (
          <motion.div key="eh-sheet" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 32 }}
            style={{ position: "fixed", left: 0, right: 0, bottom: 0, maxHeight: "88vh", background: "#fff", borderRadius: "22px 22px 0 0", zIndex: 1001, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: `0 -8px 40px ${RGBA_DARK(0.25)}` }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: RGBA_BLUE(0.3), margin: "10px auto 0", flexShrink: 0 }} />
            <Content />
          </motion.div>
        ) : (
          <motion.div key="eh-modal-wrap" style={{ position: "fixed", inset: 0, zIndex: 1001, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={closeDetails}>
            <motion.div key="eh-modal" initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }}
              transition={{ type: "spring", stiffness: 340, damping: 32 }} onClick={e => e.stopPropagation()}
              style={{ width: 460, maxWidth: "100%", maxHeight: "86vh", background: "#fff", borderRadius: 22, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: `0 30px 80px ${RGBA_DARK(0.32)}` }}>
              <Content />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
    );
  };

  /* ══════════════════════════
     HEADER
  ══════════════════════════ */
  const HeaderBar = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: isMobile ? "12px 14px" : "14px 20px" }}>
      {locLoading ? (
        <div className="eh-hbtn">
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", animation: "ehp-pulse 1.2s infinite" }} />
          <span style={{ display: isMobile ? "none" : "inline" }}>Locating…</span>
        </div>
      ) : locDenied ? (
        <button className="eh-hbtn" onClick={() => setLocationModalOpen(true)}><AlertTriangle size={14} /></button>
      ) : (
        <button onClick={handleToggleGlobal} className="eh-hbtn">
          {showGlobal ? <Globe size={13} /> : <MapPin size={13} />}
          <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry || "Nearby"}</span>
        </button>
      )}

      <div style={{ display: "flex", alignItems: "center", flex: 1, background: "rgba(255,255,255,.18)", borderRadius: 999, overflow: "hidden", border: `1.5px solid ${searchFocused ? "#fff" : "rgba(255,255,255,.32)"}`, transition: "border-color .2s" }}>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          placeholder="Search stores or products…"
          style={{ flex: 1, border: "none", outline: "none", padding: "10px 14px", fontSize: 13, color: "#fff", background: "transparent" }}
        />
        {query && (
          <button onClick={() => setQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "#fff" }}>
            <X size={13} />
          </button>
        )}
        <button style={{ width: 42, minHeight: 40, background: "rgba(255,255,255,.16)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, borderRadius: "0 999px 999px 0" }}>
          <Search size={16} color="#fff" />
        </button>
      </div>

      <button onClick={() => (isMobile ? setCatOpen(true) : setDesktopCatOpen(v => !v))} className="eh-hbtn">
        <LayoutGrid size={14} />
        <span style={{ display: isMobile ? "none" : "inline" }}>Categories</span>
      </button>

      <button onClick={goToCheckout} className="eh-hbtn" style={{ position: "relative" }}>
        <ShoppingCart size={16} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Cart"}</span>}
        {cartTotal > 0 && (
          <span style={{ position: "absolute", top: -6, right: -6, background: T.blueDark, color: "#fff", fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {cartTotal > 99 ? "99+" : cartTotal}
          </span>
        )}
      </button>
    </div>
  );

  const LocationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(5,5,5,.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(8px)" }}>
          <motion.div initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }} onClick={e => e.stopPropagation()}
            style={{ width: 320, borderRadius: 20, padding: 24, background: "#fff", boxShadow: "0 30px 80px rgba(5,5,5,.3)", textAlign: "center" }}>
            <div style={{ height: 5, borderRadius: 999, background: T.gradH, marginBottom: 20 }} />
            <div style={{ fontSize: 32, marginBottom: 10 }}>📍</div>
            <h3 style={{ fontFamily: "'Inter',sans-serif", fontWeight: 800, marginBottom: 8, color: T.black }}>Location Disabled</h3>
            <p style={{ color: T.muted, margin: "0 0 22px", fontSize: 13, lineHeight: 1.6 }}>Enable location in your browser settings to see nearby stores.</p>
            <button onClick={() => setLocationModalOpen(false)} style={{ padding: "11px 28px", borderRadius: 999, border: "none", background: T.gradH, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 12, fontFamily: "'Inter',sans-serif" }}>Got it</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ─── SIDEBAR / SHEET CATEGORY TREE — same accordion groups,
     selecting a leaf now FILTERS (handleCatSelect) instead of
     navigating to a category-details route. ─────────────────── */
  const ElectronicsCategoryTree = () => (
    <div>
      {CATEGORY_GROUPS.map((group, idx) => {
        const isOpen = openGroupIdx === idx;
        return (
          <div key={group.title} style={{ background: T.pageBg, borderRadius: 12, marginTop: 8, border: `1px solid ${T.border}` }}>
            <div onClick={() => setOpenGroupIdx(isOpen ? null : idx)} style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: T.black, cursor: "pointer", padding: 12 }}>
              {group.title}
              <motion.div animate={{ rotate: isOpen ? 90 : 0 }} style={{ marginLeft: "auto" }}><ChevronRight size={16} color={T.mutedDim} /></motion.div>
            </div>
            <AnimatePresence>
              {isOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                  <div style={{ paddingLeft: 22, paddingBottom: 8 }}>
                    {group.items.map((cat: any) => {
                      const Icon = cat.icon;
                      const sel = activeCat === cat.id;
                      return (
                        <div key={cat.id}
                          onClick={() => { handleCatSelect(cat.id); closePanel(); }}
                          style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 7, paddingBottom: 7, fontWeight: sel ? 800 : 600, color: sel ? T.blue : T.mutedSoft, cursor: "pointer", fontSize: 13 }}>
                          {Icon && <Icon size={14} />} {cat.name}
                          {sel && <Check size={13} color={T.blue} style={{ marginLeft: "auto" }} />}
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );

  const MobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(5,5,5,.55)", zIndex: 400 }} />
          <motion.div
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "22px 22px 0 0", background: "#fff", zIndex: 401, border: `1.5px solid ${T.border}`, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: T.border, borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${T.border}`, padding: "12px 20px 10px" }}>
              <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.15em", color: T.blue }}>CATEGORIES</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={15} color={T.muted} /></button>
            </div>
            <div style={{ padding: "16px 16px 32px" }}>
              <ElectronicsCategoryTree />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ══════════════════════════
     TRENDING STORES — local/global toggle mirrors GroceriesHome.
  ══════════════════════════ */
  const TrendingStoresBar = () => (
    <div style={{ marginBottom: 22, padding: isMobile ? "14px 16px" : "18px 24px", background: T.gradH, borderRadius: 18, boxShadow: `0 8px 24px ${RGBA_BLUE(0.22)}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <StoreIcon size={14} color="#fff" />
          <span style={{ fontFamily: "'Inter',sans-serif", fontWeight: 800, fontSize: 12, color: "#fff", letterSpacing: "0.04em" }}>Trending right now</span>
        </div>
        {!locLoading && !locDenied && (
          <button onClick={handleToggleGlobal}
            style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,.18)", border: "1px solid rgba(255,255,255,.35)", borderRadius: 999, padding: "5px 12px", fontSize: 11, fontWeight: 700, color: "#fff", cursor: "pointer", flexShrink: 0 }}>
            {showGlobal ? <Globe size={12} /> : <MapPin size={12} />}
            {showGlobal ? "Global" : "Local"}
          </button>
        )}
      </div>
      {trendingStores.length === 0 ? (
        <p style={{ color: "rgba(255,255,255,.85)", fontSize: 12, fontWeight: 600 }}>
          {showGlobal ? "No global stores found." : "No local stores found."}
        </p>
      ) : (
        <div style={{ display: "flex", gap: isMobile ? 14 : 22, overflowX: "auto", paddingBottom: 2 }}>
          {trendingStores.map((partner: any) => {
            const init = (partner.name ?? "S").split(" ").slice(0, 2).map((w: string) => w[0]?.toUpperCase() ?? "").join("");
            return (
              <button key={partner.brandId}
                onClick={() => goToBrand(partner.brandId)}
                style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "2px 4px" }}>
                <div style={{
                  width: isMobile ? 58 : 68, height: isMobile ? 58 : 68, borderRadius: "50%", overflow: "hidden",
                  border: "2px solid rgba(255,255,255,.5)", display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 6px 18px rgba(5,5,5,.2)",
                }}>
                  {partner.image ? (
                    <img src={partner.image} alt={partner.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", background: T.blueDark, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>{init}</span>
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", whiteSpace: "nowrap", maxWidth: 74, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {partner.name || "Store"}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  /* ══════════════════════════
     SHOP BY CATEGORY — tiles SELECT a filter, no navigation.
  ══════════════════════════ */
  const ShopByCategory = () => (
    <div style={{ marginBottom: 28 }}>
      <div onClick={() => setShopCatExpanded(v => !v)} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, cursor: "pointer", userSelect: "none" }}>
        <span style={{ fontFamily: "'Inter',sans-serif", fontWeight: 800, fontSize: isMobile ? 16 : 19, color: T.ink, letterSpacing: -0.3 }}>
          🛍️ Shop by Category
        </span>
        <motion.div animate={{ rotate: shopCatExpanded ? 0 : -90 }} transition={{ duration: 0.2 }}>
          <ChevronDown size={20} color={T.muted} />
        </motion.div>
      </div>

      <AnimatePresence initial={false}>
        {shopCatExpanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
            <div style={isMobile
              ? { display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", paddingBottom: 6 }
              : { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 18 }
            }>
              {CATEGORY_GROUPS.map(group => (
                <div key={group.title} className="eh-scg-card"
                  style={{ width: isMobile ? "78vw" : "auto", scrollSnapAlign: isMobile ? "start" : undefined }}
                  onMouseEnter={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.boxShadow = `0 8px 0 ${group.accent}22, 0 16px 38px rgba(5,5,5,.16)`;
                    el.style.borderColor = group.accent;
                    el.style.transform = "translateY(-4px)";
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.boxShadow = "0 6px 28px rgba(5,5,5,.06)";
                    el.style.borderColor = T.border;
                    el.style.transform = "translateY(0)";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: isMobile ? "12px 12px 6px" : "15px 16px 8px" }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: group.accent, flexShrink: 0 }} />
                    <span style={{ fontFamily: "'Inter',sans-serif", fontWeight: 800, fontSize: isMobile ? 12 : 14, color: T.ink, letterSpacing: -0.2 }}>
                      {group.title}
                    </span>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? 6 : 8, padding: isMobile ? "0 10px 10px" : "0 12px 12px" }}>
                    {group.items.map((cat: any) => {
                      const Icon = cat.icon;
                      const sel = activeCat === cat.id;
                      return (
                        <button key={cat.id} className={`eh-scg-tile${sel ? " sel" : ""}`}
                          style={{ "--sel-accent": group.accent } as React.CSSProperties}
                          onClick={() => handleCatSelect(cat.id)}>
                          <div className="eh-scg-tile-inner">
                            {cat.image ? (
                              <>
                                <img src={cat.image} alt={cat.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${T.black}EE, transparent 55%)` }} />
                                <span style={{ position: "absolute", bottom: isMobile ? 4 : 6, left: 0, right: 0, textAlign: "center", fontSize: isMobile ? 9 : 10.5, fontWeight: 800, color: "#fff", lineHeight: 1.2, textShadow: "0 1px 4px rgba(0,0,0,.5)", padding: "0 4px" }}>
                                  {cat.name}
                                </span>
                                {sel && (
                                  <div style={{ position: "absolute", top: 5, right: 5, width: 18, height: 18, borderRadius: "50%", background: group.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Check size={11} color="#fff" strokeWidth={3} />
                                  </div>
                                )}
                              </>
                            ) : (
                              <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, background: `${group.accent}0F`, position: "relative" }}>
                                {Icon && <Icon size={isMobile ? 22 : 26} color={group.accent} />}
                                <span style={{ fontSize: isMobile ? 9 : 10.5, fontWeight: 800, color: T.ink, textAlign: "center", padding: "0 4px", lineHeight: 1.2 }}>
                                  {cat.name}
                                </span>
                                {sel && (
                                  <div style={{ position: "absolute", top: 5, right: 5, width: 18, height: 18, borderRadius: "50%", background: group.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Check size={11} color="#fff" strokeWidth={3} />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <button onClick={() => handleCatSelect(group.items[0].id)}
                    style={{ width: "100%", padding: isMobile ? "8px 12px" : "10px 16px", background: "none", border: "none", borderTop: `1.5px solid ${T.divider}`, cursor: "pointer", fontSize: isMobile ? 11 : 12.5, fontWeight: 700, color: group.accent, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    See all <ArrowRight size={isMobile ? 12 : 14} />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  /* ══════════════════════════
     BRAND ROW HEADER — clicking navigates to the store via brandId
  ══════════════════════════ */
  const BrandRowHeader: React.FC<{ brandId: string }> = ({ brandId }) => {
    const partner  = brandMap.get(brandId);
    const shopName = partner?.name || "Store";
    const shopImg  = partner?.image;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "22px 0 12px" }}>
        <button onClick={() => goToBrand(brandId)} style={{ background: "none", cursor: "pointer", padding: 0, width: 32, height: 32, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}` }}>
          {shopImg
            ? <img src={shopImg} alt={shopName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <div style={{ width: "100%", height: "100%", background: T.blueDark, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>{shopName[0]?.toUpperCase()}</span></div>}
        </button>
        <span style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 13, color: T.ink, textDecoration: "underline", textDecorationColor: T.blue, textDecorationThickness: 2, textUnderlineOffset: 4 }}>
          {shopName}
        </span>
        <div style={{ flex: 1 }} />
        <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }}
          onClick={() => goToBrand(brandId)}
          style={{ width: 28, height: 28, borderRadius: "50%", border: "none", background: "#fff", boxShadow: "0 3px 10px rgba(0,0,0,.13)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={15} color={T.blue} />
        </motion.button>
      </div>
    );
  };

  /* ════════════════════════════════
     RENDER
  ════════════════════════════════ */
  return (
    <div style={{ minHeight: "100vh", background: T.pageBg, fontFamily: "'Inter',sans-serif" }}>
      <style>{CSS}</style>

      <header style={{ background: T.gradH, backgroundSize: "200% 100%", position: "sticky", top: 0, zIndex: 200, boxShadow: `0 4px 24px ${RGBA_BLUE(0.25)}` }}>
        <HeaderBar />
      </header>

      <MobileCategorySheet />
      <LocationModal />

      <div style={{ maxWidth: 1300, margin: "0 auto", padding: isMobile ? "0" : "0 24px" }}>

        <div style={{ padding: isMobile ? "16px 14px 0" : "24px 0 0" }}>
          <TrendingStoresBar />
          <ShopByCategory />
        </div>

        <div style={{ display: isMobile ? "block" : "grid", gridTemplateColumns: desktopCatOpen ? "280px 1fr" : "1fr", gap: 20, alignItems: "start", padding: isMobile ? "0 14px" : "0" }}>

          {!isMobile && desktopCatOpen && (
            <aside>
              <div style={{ position: "sticky", top: 90, maxHeight: "calc(100vh - 110px)", overflowY: "auto", background: "#fff", borderRadius: 16, padding: 14, border: `1.5px solid ${T.border}`, boxShadow: `0 4px 24px ${RGBA_BLUE(0.06)}` }}>
                <div style={{ marginBottom: 12 }}>
                  <p style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".2em", color: T.muted, textTransform: "uppercase" as const, marginBottom: 2 }}>Filter by</p>
                  <p style={{ fontFamily: "'Inter',sans-serif", fontSize: 17, fontWeight: 700, color: T.ink }}>Electronics</p>
                </div>
                <ElectronicsCategoryTree />
              </div>
            </aside>
          )}

          <main style={{ minWidth: 0 }}>

            {activeCat != null && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, padding: "10px 14px", background: T.bluePale, borderRadius: 14, border: `1.5px solid ${T.blue}` }}>
                <Tag size={16} color={T.blue} />
                <span style={{ fontSize: 12.5, fontWeight: 700, color: T.blue, flex: 1 }}>Browsing: {activeCatName}</span>
                <button onClick={() => setActiveCat(null)} style={{ background: "#fff", border: "none", borderRadius: "50%", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={12} color={T.blue} />
                </button>
              </motion.div>
            )}

            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 4, height: 20, borderRadius: 2, background: T.gradH }} />
                  <h2 style={{ fontFamily: "'Inter',sans-serif", fontSize: isMobile ? 15 : 19, fontWeight: 700, color: T.ink }}>
                    {activeCatName ?? (showGlobal ? "Global" : userCity ? `Near ${userCity}` : "Featured Electronics")}
                  </h2>
                  <span style={{ fontSize: 11, color: T.muted, background: T.bluePale, padding: "2px 10px", borderRadius: 999 }}>{brandProducts.length}</span>
                </div>
                {locationActive && (
                  <button onClick={handleToggleGlobal} style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? T.pageBg : T.bluePale, borderRadius: 999, padding: "5px 13px", border: `1.5px solid ${showGlobal ? T.border : RGBA_BLUE(0.3)}`, fontSize: 11, fontWeight: 700, color: showGlobal ? T.muted : T.blue, cursor: "pointer" }}>
                    {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show Local" : "Show Global"}
                  </button>
                )}
              </div>

              {productsByBrand.length === 0 ? (
                <div style={{ textAlign: "center", padding: "52px 20px" }}>
                  <div style={{ fontSize: 46, marginBottom: 12 }}>🛍️</div>
                  <p style={{ fontFamily: "'Inter',sans-serif", fontSize: 15, color: T.ink, marginBottom: 6 }}>
                    {activeCat != null
                      ? `No products found in "${activeCatName}"`
                      : locationActive
                        ? (showGlobal ? "No global products found." : "No local products found.")
                        : "No products found"}
                  </p>
                  <p style={{ fontSize: 12, color: T.muted }}>
                    {activeCat != null ? "Try a different category or check back soon." : "Products appear once stores go live."}
                  </p>
                </div>
              ) : (
                productsByBrand.map(group => (
                  <div key={group.brandId} style={{ marginBottom: 8 }}>
                    <BrandRowHeader brandId={group.brandId} />
                    <div style={{ display: isMobile ? "grid" : "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(auto-fill,minmax(200px,1fr))", gap: isMobile ? 12 : 18 }}>
                      {group.items.slice(0, 32).map((item: any) => (
                        <ProductCard key={item.listingId} item={item} />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </main>
        </div>
      </div>

      <DetailView />
    </div>
  );
};

export default ElectronicsHome;